import {
  Button,
  Grid,
  Modal,
  NumberInput,
  RemoveScroll,
  ScrollArea,
  Select,
  Text
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { showNotification } from '../../../utils/Notification/Notification';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from '@mantine/form';
import { requiredFieldFormValidator } from '../../../utils/validators/formValidators';
import { IconCurrencyDollar } from '@tabler/icons-react';
import { IOrder, IOrderPaymentLines } from '../../../interfaces/Order';
import PaymentConfigEnum from '../../../enum/PaymentConfig';
import { formatMoneyString } from '../../../utils/strings';
import { ICashierBox } from '../../../interfaces/Store';
import { createPaymentLineByOrderId } from '../../../services/order.service';
import LoaderOverlay from '../../../components/LoaderOverlay/LoaderOverlay';

export default function MarkAsPaidModal({
  opened,
  close,
  title,
  order,
  onCloseModal,
  onSubmitModal = () => {},
  cashierboxes = []
}: {
  order: IOrder;
  opened: boolean;
  close: () => void;
  onCloseModal: () => void;
  onSubmitModal: () => void;
  title: string;
  cashierboxes: ICashierBox[];
}) {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(['Notifications', 'CommonButtons', 'FieldName']);
  const { user } = useSelector((state: any) => state.user);
  const form = useForm({
    initialValues: async () => {
      const initialCashierBox =
        typeof user.cashierBox === 'string' && user.cashierBox
          ? user.cashierBox
          : (cashierboxes || [])[0]?._id;
      const initialValues = {
        amountPaid: 0,
        paymentMethod: 'transfer',
        cashierBox: initialCashierBox
      };
      return initialValues;
    },
    validate: {
      amountPaid: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      paymentMethod: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      cashierBox: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      }
    }
  });

  const onSubmit = async (values: any) => {
    try {
      if (loading) return;
      setLoading(true);
      await createPaymentLineByOrderId(order._id || '', values);
      if (onSubmitModal) {
        await onSubmitModal();
      }
      showNotification({ type: 'Default', title: t('Notifications:success') });
      close();
      return true;
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      setLoading(false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (order) {
      const initialCashierbox =
        typeof user.cashierBox === 'string' && user.cashierBox
          ? user.cashierBox
          : (cashierboxes || [])[0]?._id;
      const obj = {
        amountPaid:
          order?.total -
          order?.paymentLines?.reduce((prev: number, line: IOrderPaymentLines) => {
            return prev + line.amountPaid;
          }, 0),
        paymentMethod: 'transfer',
        cashierBox: initialCashierbox
      };
      form.setValues(obj);
      form.resetDirty(obj);
    }
  }, [order, cashierboxes]);

  return (
    <Modal
      opened={opened}
      onClose={() => {
        onCloseModal && onCloseModal();
        close();
      }}
      className={RemoveScroll.classNames.fullWidth}
      title={title}
      scrollAreaComponent={ScrollArea.Autosize}>
      <form onSubmit={form.onSubmit(onSubmit)}>
        <LoaderOverlay visible={loading} />
        <Grid>
          <Grid.Col span={12}>
            <Text size="md" fw="bold">
              {t('FieldName:amountDue')}:{' '}
              {formatMoneyString(
                order?.total -
                  order?.paymentLines?.reduce((prev: number, line: IOrderPaymentLines) => {
                    return prev + line.amountPaid;
                  }, 0)
              )}
            </Text>
          </Grid.Col>
          <Grid.Col span={12}>
            <NumberInput
              {...form.getInputProps('amountPaid')}
              placeholder={t('FieldName:amount')}
              allowLeadingZeros={false}
              allowNegative={false}
              label={t('FieldName:amount')}
              leftSection={<IconCurrencyDollar />}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Select
              {...form.getInputProps('paymentMethod')}
              allowDeselect={false}
              label={t('FieldName:paymentMethod')}
              placeholder={t('FieldName:paymentMethod')}
              data={Object.keys(PaymentConfigEnum).reduce((prev, paymentConfigKey: any) => {
                if (PaymentConfigEnum[paymentConfigKey] !== 'delivery') {
                  prev.push({
                    value: PaymentConfigEnum[paymentConfigKey],
                    label: t(`FieldName:paymentMethods.${PaymentConfigEnum[paymentConfigKey]}`)
                  });
                }
                return prev;
              }, [])}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Select
              {...form.getInputProps('cashierBox')}
              allowDeselect={false}
              label={t('FieldName:cashierBox')}
              placeholder={t('FieldName:cashierBox')}
              data={cashierboxes.map((cashierbox) => {
                return {
                  label: cashierbox.name,
                  value: cashierbox._id,
                  key: cashierbox._id
                };
              })}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Button fullWidth type="submit" disabled={loading}>
              {t('CommonButtons:submit')}
            </Button>
          </Grid.Col>
        </Grid>
      </form>
    </Modal>
  );
}
